import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { plistOrderService, PListOrderDto } from '../../../../services/plistOrderService';

/** Splitter OrderNoERPs fra backend (STRING_AGG med ', ', sortert på OrderNo) til enkeltnumre. */
export const splitOrderNoERPs = (value?: string | null): string[] =>
  (value ?? '').split(', ').map(s => s.trim()).filter(Boolean);

interface OrderListBadgeProps {
  orderNoERPs: string[];
  /** Når satt hentes kunde/mottaker per ordre første gang listen åpnes (samplukk). */
  companyNo?: string;
  pListId?: number;
  className?: string;
}

// «+N ordrer» for plukklister med flere ordrer (TW 36416444). Operatørene bruker berøringsskjerm,
// så hele listen åpnes ved trykk — ikke hover-tooltip. Klikk stoppes her slik at trykket verken
// starter plukket eller velger kortet i batch-modus.
export const OrderListBadge: React.FC<OrderListBadgeProps> = ({ orderNoERPs, companyNo, pListId, className = '' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState<PListOrderDto[] | null>(null);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  // Kunde/mottaker per ordre hentes ved første åpning — ikke for hvert kort i listen.
  // Feiler kallet, vises bare ordrenumrene som før.
  useEffect(() => {
    if (!open || orders !== null || loadingOrders || !companyNo || !pListId) return;
    setLoadingOrders(true);
    plistOrderService
      .getByPListId(companyNo, pListId)
      .then(setOrders)
      .catch(err => {
        console.error('Error loading orders for pick list:', err);
        setOrders([]);
      })
      .finally(() => setLoadingOrders(false));
  }, [open, orders, loadingOrders, companyNo, pListId]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const extra = orderNoERPs.length - 1;
  if (extra < 1) return null;

  const label = extra === 1
    ? t('warehouse.modules.picking.card.moreOrdersSingular', {}, '+1 ordre')
    : t('warehouse.modules.picking.card.moreOrders', { '0': extra }, `+${extra} ordrer`);
  const heading = t('warehouse.modules.picking.card.allOrders', {}, 'Ordrer i plukklisten');

  const orderByErpNo = new Map((orders ?? []).map(o => [o.orderNoERP ?? '', o]));
  // Kunde = ordrens kunde; mottaker vises i tillegg bare når den er en annen
  const getParties = (orderNo: string): { customer: string | null; recipient: string | null } => {
    const order = orderByErpNo.get(orderNo);
    if (!order) return { customer: null, recipient: null };
    const customer = order.customerName?.trim() || order.delName?.trim() || null;
    const recipient = order.delName?.trim() || null;
    return {
      customer,
      recipient: recipient && recipient.toLowerCase() !== customer?.toLowerCase() ? recipient : null,
    };
  };

  return (
    <span
      ref={containerRef}
      className={`relative inline-flex flex-shrink-0 ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* before:-inset-y-3 utvider trykkflaten til ~44 px uten å gjøre raden høyere */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-label={`${label} – ${heading}`}
        className="relative inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold bg-gray-100 text-gray-700 whitespace-nowrap hover:bg-gray-200 transition-colors before:content-[''] before:absolute before:-inset-x-1 before:-inset-y-3"
      >
        {label}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 z-30 min-w-[16rem] max-w-[80vw] sm:max-w-md max-h-80 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg p-2 text-xs text-gray-800">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase text-gray-400 mb-1">
            {heading}
            {loadingOrders && <Loader2 size={10} className="animate-spin" />}
          </div>
          <ul className="divide-y divide-gray-100">
            {orderNoERPs.map(orderNo => {
              const { customer, recipient } = getParties(orderNo);
              return (
                <li key={orderNo} className="py-1.5 first:pt-0 last:pb-0">
                  <div className="font-mono break-all">{orderNo}</div>
                  {customer && <div className="font-semibold text-gray-900 break-words">{customer}</div>}
                  {recipient && <div className="text-gray-500 break-words">{recipient}</div>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </span>
  );
};
